import AdminPermission from "components/admin/AdminPermission";
import useAdminAccess from "hooks/useAdminAccess";
import {
  EloTier,
  EloTierInput,
  LifecycleStatus,
  gamificationApi,
} from "api/admin/gamification";
import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  SectionCard,
  dateTimeLocal,
  errorMessage,
  fieldClass,
  formatLabel,
} from "./components";

type TierForm = {
  code: string;
  name: string;
  lowerLimit: string;
  upperLimit: string;
  maintenanceElo: string;
  freezeDays: string;
  graceDays: string;
  sortOrder: string;
  status: LifecycleStatus;
  market: string;
  effectiveFrom: string;
  effectiveTo: string;
  perks: TierPerkDraft[];
  rewardDefinitionIds: string[];
  reason: string;
};

type TierPerkDraft = {
  clientId: string;
  code: string;
  title: string;
  description: string;
};

let nextPerkId = 0;

const makePerkDraft = (
  perk?: Partial<Omit<TierPerkDraft, "clientId">>,
): TierPerkDraft => ({
  clientId: `tier-perk-${nextPerkId++}`,
  code: perk?.code ?? "",
  title: perk?.title ?? "",
  description: perk?.description ?? "",
});

const formFromTier = (tier?: EloTier): TierForm => ({
  code: tier?.code ?? "",
  name: tier?.name ?? "",
  lowerLimit: String(tier?.lowerLimit ?? ""),
  upperLimit: String(tier?.upperLimit ?? ""),
  maintenanceElo: String(tier?.maintenanceElo ?? tier?.lowerLimit ?? ""),
  freezeDays: String(tier?.freezeDays ?? 0),
  graceDays: String(tier?.graceDays ?? 0),
  sortOrder: String(tier?.sortOrder ?? 0),
  status: tier?.status ?? "active",
  market: tier?.market ?? "",
  effectiveFrom: dateTimeLocal(tier?.effectiveFrom),
  effectiveTo: tier?.effectiveTo ? dateTimeLocal(tier.effectiveTo) : "",
  perks: tier?.perks?.map((perk) => makePerkDraft(perk)) ?? [],
  rewardDefinitionIds: (tier?.rewardDefinitionIds ??
    (tier?.rewardDefinitionId ? [tier.rewardDefinitionId] : [])).map(String),
  reason: "",
});

const perksFromDrafts = (perks: TierPerkDraft[]) =>
  perks.map((perk, index) => {
    const title = perk.title.trim();
    return {
      title,
      code:
        perk.code ||
        title.toLowerCase().replace(/\W+/g, "_").replace(/^_+|_+$/g, "") ||
        `perk_${index + 1}`,
      description: perk.description.trim(),
      sortOrder: index,
    };
  });

export default function Tiers() {
  const { can } = useAdminAccess();
  const canReadOptions = can("gamification.overview.view") || can("gamification.eloTiers.view");
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EloTier | null | undefined>();
  const [form, setForm] = useState<TierForm>(formFromTier());
  const tiers = useQuery("gamification-tiers", gamificationApi.listTiers, { enabled: can("gamification.eloTiers.view"),
    retry: false,
  });
  const rewards = useQuery(
    "gamification-reward-definitions",
    gamificationApi.listRewardDefinitions,
    { enabled: can("gamification.rewardDefinitions.view"), retry: false },
  );
  const options = useQuery(
    "gamification-configuration-options",
    gamificationApi.getConfigurationOptions,
    { enabled: canReadOptions, retry: false },
  );

  const close = () => setEditing(undefined);
  const save = useMutation(
    async ({ tier, input }: { tier?: EloTier | null; input: EloTierInput }) =>
      tier
        ? gamificationApi.updateTier(tier.id, input)
        : gamificationApi.createTier({
            ...input,
            code:
              form.code.trim() ||
              form.name.trim().toLowerCase().replace(/\W+/g, "_"),
          }),
    {
      onSuccess: () => {
        toast.success(editing ? "Tier updated" : "Tier created");
        queryClient.invalidateQueries("gamification-tiers");
        queryClient.invalidateQueries("gamification-tiers-overview");
        close();
      },
      onError: (error) => {
        toast.error(errorMessage(error));
      },
    },
  );

  const open = (tier: EloTier | null) => {
    setEditing(tier);
    setForm(formFromTier(tier ?? undefined));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate({
      tier: editing,
      input: {
        name: form.name.trim(),
        lowerLimit: Number(form.lowerLimit),
        upperLimit: Number(form.upperLimit),
        maintenanceElo: Number(form.maintenanceElo),
        freezeDays: Number(form.freezeDays),
        graceDays: Number(form.graceDays),
        sortOrder: Number(form.sortOrder),
        status: form.status,
        market: form.market || null,
        effectiveFrom: new Date(form.effectiveFrom).toISOString(),
        effectiveTo: form.effectiveTo
          ? new Date(form.effectiveTo).toISOString()
          : null,
        perks: perksFromDrafts(form.perks),
        rewardDefinitionIds: form.rewardDefinitionIds.map(Number),
        reason: form.reason || undefined,
      },
    });
  };

  const updatePerk = (
    clientId: string,
    patch: Partial<TierPerkDraft>,
  ) => {
    setForm((current) => ({
      ...current,
      perks: current.perks.map((perk) =>
        perk.clientId === clientId ? { ...perk, ...patch } : perk,
      ),
    }));
  };

  const movePerk = (index: number, direction: -1 | 1) => {
    setForm((current) => {
      const destination = index + direction;
      if (destination < 0 || destination >= current.perks.length) return current;
      const perks = [...current.perks];
      [perks[index], perks[destination]] = [perks[destination], perks[index]];
      return { ...current, perks };
    });
  };

  return (
    <>
      <SectionCard
        title="Tiers"
        description="Set the ELO points a user needs to reach each tier."
        action={
          <AdminPermission permission={"gamification.eloTiers.create"}>
            <Button onClick={() => open(null)}>
              <i className="fa fa-plus" />
              Add tier
            </Button>
          </AdminPermission>
        }
      >
        {tiers.isLoading || rewards.isLoading || options.isLoading ? (
          <LoadingState rows={4} />
        ) : tiers.error || rewards.error || options.error ? (
          <ErrorState
            error={tiers.error || rewards.error || options.error}
            onRetry={() => {
              if (can("gamification.eloTiers.view")) tiers.refetch();
              if (can("gamification.rewardDefinitions.view")) rewards.refetch();
              if (canReadOptions) options.refetch();
            }}
          />
        ) : !tiers.data?.tiers.length ? (
          <EmptyState
            title="No tiers configured"
            body="Add the first tier and choose its ELO range."
            icon="fa-layer-group"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-left">
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-5 py-3">Tier</th>
                  <th className="px-5 py-3">ELO & maintenance</th>
                  <th className="px-5 py-3">Perks</th>
                  <th className="px-5 py-3">Rewards</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tiers.data.tiers.map((tier) => (
                  <tr className="hover:bg-gray-50/70" key={tier.id}>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-gray-950">{tier.name}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {options.data?.markets.find(
                          (market) => market.code === tier.market,
                        )?.name ?? tier.market ?? "All markets"}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-sm font-semibold text-gray-800">
                      {tier.lowerLimit.toLocaleString()} –{" "}
                      {tier.upperLimit.toLocaleString()}
                      <p className="mt-1 text-xs font-normal text-gray-500">
                        Maintain {Number(tier.maintenanceElo ?? tier.lowerLimit).toLocaleString()} · {tier.memberCount ?? 0} members
                      </p>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">
                      {tier.perks?.length
                        ? tier.perks.map((perk) => perk.title).join(", ")
                        : "No perks"}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">
                      {(tier.rewardDefinitionIds ??
                        (tier.rewardDefinitionId ? [tier.rewardDefinitionId] : []))
                        .map((id) => rewards.data?.rewards.find((reward) => reward.id === id)?.name)
                        .filter(Boolean)
                        .join(", ") || "No rewards"}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <AdminPermission permission={"gamification.eloTiers.edit"}>
                        <Button variant="quiet" onClick={() => open(tier)}>
                          Edit
                        </Button>
                      </AdminPermission>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <AdminPermission
        permission={editing ? "gamification.eloTiers.edit" : "gamification.eloTiers.create"}
      >
        <Drawer
          open={editing !== undefined}
          title={editing ? `Edit ${editing.name}` : "Add tier"}
          description="Choose the tier name, ELO range and perks."
          onClose={close}
        >
          <form className="space-y-5" onSubmit={submit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Tier name" required>
                <input
                  required
                  minLength={2}
                  maxLength={128}
                  className={fieldClass}
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                label="Maintenance ELO"
                hint="Keep this many points to retain the tier."
                required
              >
                <input
                  required
                  min={0}
                  step={1}
                  type="number"
                  className={fieldClass}
                  value={form.maintenanceElo}
                  onChange={(event) =>
                    setForm({ ...form, maintenanceElo: event.target.value })
                  }
                />
              </Field>
              <Field
                label="Freeze (days)"
                hint="No downgrade during this period after entry."
              >
                <input
                  min={0}
                  max={365}
                  step={1}
                  type="number"
                  className={fieldClass}
                  value={form.freezeDays}
                  onChange={(event) => setForm({ ...form, freezeDays: event.target.value })}
                />
              </Field>
              <Field
                label="Grace period (days)"
                hint="Time to recover after falling below maintenance."
              >
                <input
                  min={0}
                  max={365}
                  step={1}
                  type="number"
                  className={fieldClass}
                  value={form.graceDays}
                  onChange={(event) => setForm({ ...form, graceDays: event.target.value })}
                />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Lower ELO limit" required>
                <input
                  required
                  min={0}
                  step={1}
                  type="number"
                  className={fieldClass}
                  value={form.lowerLimit}
                  onChange={(event) => setForm({ ...form, lowerLimit: event.target.value })}
                />
              </Field>
              <Field label="Upper ELO limit" required>
                <input
                  required
                  min={form.lowerLimit ? Number(form.lowerLimit) + 1 : 1}
                  step={1}
                  type="number"
                  className={fieldClass}
                  value={form.upperLimit}
                  onChange={(event) => setForm({ ...form, upperLimit: event.target.value })}
                />
              </Field>
            </div>
            <fieldset className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <legend className="text-sm font-semibold text-gray-800">Tier perks</legend>
                  <p className="mt-1 text-xs text-gray-500">
                    Add each benefit separately so its name and explanation stay clear to
                    members.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      perks: [...current.perks, makePerkDraft()],
                    }))
                  }
                >
                  <i className="fa fa-plus" />
                  Add perk
                </Button>
              </div>
              {!form.perks.length ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-center text-sm text-gray-500">
                  No perks added. This tier can still have a reward.
                </div>
              ) : (
                form.perks.map((perk, index) => (
                  <div
                    key={perk.clientId}
                    className="rounded-xl border border-gray-200 bg-gray-50 p-4"
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label={`Perk ${index + 1} name`} required>
                        <input
                          required
                          minLength={2}
                          maxLength={128}
                          className={fieldClass}
                          placeholder="Priority support"
                          value={perk.title}
                          onChange={(event) =>
                            updatePerk(perk.clientId, {
                              title: event.target.value,
                            })
                          }
                        />
                      </Field>
                      <Field
                        label="Member-facing description"
                        hint="Explain exactly what the member receives."
                        required
                      >
                        <input
                          required
                          minLength={2}
                          maxLength={255}
                          className={fieldClass}
                          placeholder="Get faster help from the support team."
                          value={perk.description}
                          onChange={(event) =>
                            updatePerk(perk.clientId, {
                              description: event.target.value,
                            })
                          }
                        />
                      </Field>
                    </div>
                    <div className="mt-3 flex justify-end gap-1">
                      <Button
                        type="button"
                        variant="quiet"
                        disabled={index === 0}
                        aria-label={`Move ${perk.title || `perk ${index + 1}`} up`}
                        onClick={() => movePerk(index, -1)}
                      >
                        <i className="fa fa-arrow-up" />
                      </Button>
                      <Button
                        type="button"
                        variant="quiet"
                        disabled={index === form.perks.length - 1}
                        aria-label={`Move ${perk.title || `perk ${index + 1}`} down`}
                        onClick={() => movePerk(index, 1)}
                      >
                        <i className="fa fa-arrow-down" />
                      </Button>
                      <Button
                        type="button"
                        variant="quiet"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            perks: current.perks.filter(
                              (candidate) => candidate.clientId !== perk.clientId,
                            ),
                          }))
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </fieldset>
            <fieldset>
              <legend className="text-sm font-semibold text-gray-800">Tier rewards</legend>
              <p className="mt-1 text-xs text-gray-500">
                Choose every reward unlocked when a member reaches this tier.
              </p>
              <div className="mt-3 max-h-52 space-y-2 overflow-y-auto rounded-xl border border-gray-200 p-3">
                {!rewards.data?.rewards.filter((reward) => reward.status !== "archived")
                  .length ? (
                  <p className="text-sm text-gray-500">No active rewards available.</p>
                ) : (
                  rewards.data?.rewards
                    .filter((reward) => reward.status !== "archived")
                    .map((reward) => (
                      <label
                        className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-2 hover:bg-gray-50"
                        key={reward.id}
                      >
                        <span className="text-sm text-gray-700">
                          {reward.name}{" "}
                          <span className="text-xs text-gray-400">
                            · {reward.maxUses} uses
                          </span>
                        </span>
                        <input
                          type="checkbox"
                          checked={form.rewardDefinitionIds.includes(String(reward.id))}
                          onChange={() =>
                            setForm((current) => ({
                              ...current,
                              rewardDefinitionIds: current.rewardDefinitionIds.includes(
                                String(reward.id),
                              )
                                ? current.rewardDefinitionIds.filter(
                                    (id) => id !== String(reward.id),
                                  )
                                : [...current.rewardDefinitionIds, String(reward.id)],
                            }))
                          }
                        />
                      </label>
                    ))
                )}
              </div>
            </fieldset>
            <details className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <summary className="cursor-pointer text-sm font-semibold text-gray-700">
                More options
              </summary>
              <div className="mt-5 space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Tier code" hint="Created automatically from the name.">
                    <input
                      required
                      disabled={Boolean(editing)}
                      minLength={2}
                      maxLength={64}
                      className={fieldClass}
                      value={form.code || form.name.toLowerCase().replace(/\W+/g, "_")}
                      onChange={(event) => setForm({ ...form, code: event.target.value })}
                    />
                  </Field>
                  <Field label="Status">
                    <select
                      className={fieldClass}
                      value={form.status}
                      onChange={(event) =>
                        setForm({ ...form, status: event.target.value as LifecycleStatus })
                      }
                    >
                      {["draft", "scheduled", "active", "archived"].map((status) => (
                        <option key={status} value={status}>
                          {formatLabel(status)}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field
                  label="Market scope"
                  hint="Global tiers apply unless a market-specific version is configured."
                >
                  <select
                    className={fieldClass}
                    value={form.market}
                    onChange={(event) => setForm({ ...form, market: event.target.value })}
                  >
                    {options.data?.markets.map((market) => (
                      <option key={market.code ?? "global"} value={market.code ?? ""}>
                        {market.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Starts">
                    <input
                      required
                      type="datetime-local"
                      className={fieldClass}
                      value={form.effectiveFrom}
                      onChange={(event) =>
                        setForm({ ...form, effectiveFrom: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="Ends">
                    <input
                      min={form.effectiveFrom}
                      type="datetime-local"
                      className={fieldClass}
                      value={form.effectiveTo}
                      onChange={(event) =>
                        setForm({ ...form, effectiveTo: event.target.value })
                      }
                    />
                  </Field>
                </div>
                <Field label="Reason for change">
                  <textarea
                    rows={2}
                    className={fieldClass}
                    value={form.reason}
                    onChange={(event) => setForm({ ...form, reason: event.target.value })}
                  />
                </Field>
              </div>
            </details>
            <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
              <Button type="button" variant="secondary" onClick={close}>
                Cancel
              </Button>
              <Button disabled={save.isLoading} type="submit">
                {save.isLoading ? "Saving…" : editing ? "Save changes" : "Add tier"}
              </Button>
            </div>
          </form>
        </Drawer>
      </AdminPermission>
    </>
  );
}
